import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS = {
  aguardando_nf: { l: "Aguardando NF", c: "bg-[#FFCC00]" },
  pendente: { l: "Aguardando NF", c: "bg-[#FFCC00]" }, // legacy
  nf_recebida: { l: "NF Recebida", c: "bg-[#142766] text-white" },
  emitida: { l: "NF Recebida", c: "bg-[#142766] text-white" }, // legacy
  nf_validada: { l: "Validada", c: "bg-[#00A859] text-white" },
  nf_rejeitada: { l: "Rejeitada", c: "bg-[#E60000] text-white" },
  nf_paga: { l: "Paga", c: "bg-foreground text-white" },
  atrasada: { l: "Atrasada", c: "bg-[#E60000] text-white" },
  acumulada: { l: "Acumulada", c: "bg-[#142766] text-white" },
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotasFiscaisEmpresa() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [pendencias, setPendencias] = useState([]);
  const [prazos, setPrazos] = useState([]);
  const [cobrando, setCobrando] = useState(null);
  const [dash, setDash] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroPeriodo, setFiltroPeriodo] = useState("");

  const [rejeitar, setRejeitar] = useState(null); // pendencia
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  const [pagar, setPagar] = useState(null);
  const [formPagar, setFormPagar] = useState({ valor_pago: 0, data_pagamento: "", comprovante: null });

  const [fechamentoOpen, setFechamentoOpen] = useState(false);
  const [janela, setJanela] = useState({ inicio: "", fim: "" });

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  const loadAll = async () => {
    if (!empresaId) return;
    const params = new URLSearchParams();
    if (filtroStatus) params.set("status", filtroStatus);
    if (filtroPeriodo) params.set("periodo", filtroPeriodo);
    const qs = params.toString() ? "?" + params.toString() : "";
    try {
      const [p, d, pz] = await Promise.all([
        api.get(`/empresas/${empresaId}/pendencias-nf${qs}`),
        api.get(`/empresas/${empresaId}/dashboard-fiscal${filtroPeriodo ? "?periodo=" + filtroPeriodo : ""}`),
        api.get(`/empresas/${empresaId}/prazos-nf`),
      ]);
      setPendencias(p.data);
      setDash(d.data);
      setPrazos(pz.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); }, [empresaId, filtroStatus, filtroPeriodo]); // eslint-disable-line

  const cobrar = async (p) => {
    setCobrando(p.id);
    try {
      await api.post(`/empresas/${empresaId}/pendencias-nf/${p.id}/cobrar`);
      toast.success(`Cobrança enviada por WhatsApp a ${p.montador_nome || "montador"}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCobrando(null); }
  };

  const cobrarTodasAtrasadas = async () => {
    setCobrando("massa");
    try {
      const r = await api.post(`/empresas/${empresaId}/pendencias-nf/cobrar-atrasadas`);
      const d = r.data;
      toast.success(`${d.enviadas} cobrança(s) enviada(s) · ${d.puladas_24h} já cobradas hoje · ${d.sem_telefone} sem telefone`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCobrando(null); }
  };

  const baixar = async (p, tipo = "nf") => {
    const url = tipo === "nf"
      ? `${API}/api/empresas/${empresaId}/pendencias-nf/${p.id}/download`
      : `${API}/api/empresas/${empresaId}/pendencias-nf/${p.id}/comprovante`;
    const token = localStorage.getItem("token");
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Erro no download"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = tipo === "nf" ? `NF_${p.nf_numero}_${p.periodo}.pdf` : `Comprovante_${p.periodo}.pdf`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  const validar = async (p) => {
    if (!window.confirm(`Validar NF ${p.nf_numero} de ${p.montador_nome}?`)) return;
    try {
      await api.post(`/empresas/${empresaId}/pendencias-nf/${p.id}/validar`);
      toast.success("NF validada");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarRejeicao = async () => {
    if (!motivoRejeicao.trim()) { toast.error("Informe o motivo"); return; }
    try {
      await api.post(`/empresas/${empresaId}/pendencias-nf/${rejeitar.id}/rejeitar`,
        { motivo: motivoRejeicao });
      toast.success("NF rejeitada");
      setRejeitar(null); setMotivoRejeicao("");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarPagamento = async () => {
    if (!formPagar.valor_pago || !formPagar.data_pagamento) {
      toast.error("Informe valor e data do pagamento"); return;
    }
    try {
      const fd = new FormData();
      fd.append("valor_pago", String(formPagar.valor_pago));
      fd.append("data_pagamento", formPagar.data_pagamento);
      if (formPagar.comprovante) fd.append("comprovante", formPagar.comprovante);
      await api.post(`/empresas/${empresaId}/pendencias-nf/${pagar.id}/marcar-pago`, fd);
      toast.success("Pagamento registrado");
      setPagar(null); setFormPagar({ valor_pago: 0, data_pagamento: "", comprovante: null });
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const executarFechamento = async () => {
    if (!janela.inicio || !janela.fim) { toast.error("Informe início e fim"); return; }
    try {
      const r = await api.post(`/empresas/${empresaId}/fechamento-fiscal/executar`, janela);
      toast.success(`${r.data.pendencias_criadas} pendência(s) criada(s)`);
      setFechamentoOpen(false); setJanela({ inicio: "", fim: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">Financeiro · Empresa</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Notas Fiscais</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Gestão completa: validar, rejeitar, marcar como pago. Fechamentos automáticos via cron diário (02:30).
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => {
            if (!empresaId) return;
            const params = new URLSearchParams({ formato: "csv" });
            if (filtroPeriodo) params.set("periodo", filtroPeriodo);
            if (filtroStatus) params.set("status", filtroStatus);
            const token = localStorage.getItem("token");
            fetch(`${API}/api/empresas/${empresaId}/relatorios/fiscal?${params}`,
              { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob()).then(b => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b); a.download = `relatorio_fiscal.csv`; a.click();
                URL.revokeObjectURL(a.href);
              });
          }} variant="outline" className="btn-sharp" data-testid="btn-export-csv">↓ CSV</Button>
          <Button onClick={() => {
            if (!empresaId) return;
            const params = new URLSearchParams({ formato: "pdf" });
            if (filtroPeriodo) params.set("periodo", filtroPeriodo);
            if (filtroStatus) params.set("status", filtroStatus);
            const token = localStorage.getItem("token");
            fetch(`${API}/api/empresas/${empresaId}/relatorios/fiscal?${params}`,
              { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob()).then(b => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b); a.download = `relatorio_fiscal.pdf`; a.click();
                URL.revokeObjectURL(a.href);
              });
          }} variant="outline" className="btn-sharp" data-testid="btn-export-pdf">↓ PDF</Button>
          <Button onClick={() => setFechamentoOpen(true)} disabled={!empresaId}
            variant="outline" className="btn-sharp" data-testid="btn-fechamento-manual">
            Fechamento manual
          </Button>
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Label className="label-cap">Empresa</Label>
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="mt-1 max-w-md" data-testid="nf-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Dashboard fiscal */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dash-fiscal">
          <DashCard label="Aguardando" valor={dash.total_aguardando} qtd={dash.indicadores.aguardando_nf.qtd + dash.indicadores.atrasada.qtd}
            color="text-[#FFCC00]" />
          <DashCard label="A pagar" valor={dash.total_a_pagar} qtd={dash.indicadores.nf_validada.qtd}
            color="text-[#00A859]" />
          <DashCard label="Pago" valor={dash.total_pago} qtd={dash.indicadores.nf_paga.qtd}
            color="text-foreground" />
          <DashCard label="Em atraso" valor={dash.total_atraso} qtd={dash.indicadores.atrasada.qtd}
            color="text-[#E60000]" />
        </div>
      )}

      {/* Prazos de NF — cobrança */}
      {prazos.length > 0 && (
        <div className="border-2 border-[#FFCC00] bg-[#FFFBEB] p-5 mb-6" data-testid="prazos-nf">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="label-cap">⏰ Prazos de NF · {prazos.length} pendência(s) aguardando emissão</div>
            {prazos.some(p => (p.dias_restantes ?? 0) < 0 || p.status === "atrasada") && (
              <Button size="sm" className="btn-sharp bg-[#E60000] hover:bg-[#c40000] text-white h-7 text-[10px]"
                disabled={cobrando === "massa"}
                onClick={cobrarTodasAtrasadas} data-testid="nf-cobrar-todas">
                {cobrando === "massa" ? "Enviando…" : `Cobrar todas atrasadas (${prazos.filter(p => (p.dias_restantes ?? 0) < 0 || p.status === "atrasada").length})`}
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {prazos.map(p => {
              const atrasada = (p.dias_restantes ?? 0) < 0 || p.status === "atrasada";
              const urgente = !atrasada && (p.dias_restantes ?? 99) <= 5;
              const badge = atrasada
                ? { t: `${Math.abs(p.dias_restantes ?? 0)} dia(s) em atraso`, c: "bg-[#E60000] text-white" }
                : urgente
                  ? { t: `vence em ${p.dias_restantes} dia(s)`, c: "bg-[#FFCC00]" }
                  : { t: `vence em ${p.dias_restantes} dia(s)`, c: "bg-[#F4F4F5]" };
              const cobradaHoje = p.cobranca_nf_em &&
                (Date.now() - new Date(p.cobranca_nf_em).getTime()) < 86400000;
              return (
                <div key={p.id} className="flex flex-wrap items-center gap-3 bg-white border border-border p-3"
                  data-testid={`prazo-${p.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <span className="font-medium">{p.montador_nome || "Montador"}</span>
                    <span className="text-xs text-muted-foreground"> · {p.periodo} · R$ {Number(p.valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${badge.c}`}>{badge.t}</span>
                  <span className="text-[11px] text-muted-foreground">
                    venc. {new Date(p.vencimento).toLocaleDateString("pt-BR")}
                    {p.cobranca_nf_em && ` · cobrado ${new Date(p.cobranca_nf_em).toLocaleDateString("pt-BR")}`}
                  </span>
                  <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                    disabled={cobrando === p.id || cobradaHoje}
                    onClick={() => cobrar(p)} data-testid={`nf-cobrar-${p.id}`}>
                    {cobradaHoje ? "Cobrado hoje" : cobrando === p.id ? "Enviando…" : "Cobrar WhatsApp"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="label-cap">Status</Label>
          <Select value={filtroStatus || "_all"} onValueChange={(v) => setFiltroStatus(v === "_all" ? "" : v)}>
            <SelectTrigger className="mt-1 w-[180px]" data-testid="nf-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todos</SelectItem>
              <SelectItem value="aguardando_nf">Aguardando NF</SelectItem>
              <SelectItem value="nf_recebida">NF Recebida</SelectItem>
              <SelectItem value="nf_validada">Validada</SelectItem>
              <SelectItem value="nf_rejeitada">Rejeitada</SelectItem>
              <SelectItem value="nf_paga">Paga</SelectItem>
              <SelectItem value="atrasada">Atrasada</SelectItem>
              <SelectItem value="acumulada">Acumulada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-cap">Período</Label>
          <Input value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)}
            placeholder="2026-05" className="mt-1 w-[140px]" data-testid="nf-filter-periodo" />
        </div>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap font-medium">Montador</th>
              <th className="text-left p-3 label-cap font-medium">Período</th>
              <th className="text-left p-3 label-cap font-medium">Valor</th>
              <th className="text-left p-3 label-cap font-medium">Venc.</th>
              <th className="text-left p-3 label-cap font-medium">NF</th>
              <th className="text-left p-3 label-cap font-medium">Envio</th>
              <th className="text-left p-3 label-cap font-medium">Status</th>
              <th className="text-left p-3 label-cap font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {pendencias.length === 0 && (
              <tr><td colSpan={8} className="p-12 text-center text-sm text-muted-foreground">Sem pendências para os filtros aplicados</td></tr>
            )}
            {pendencias.map(p => {
              const status = STATUS[p.status] || STATUS.aguardando_nf;
              const canon = ({pendente:"aguardando_nf",emitida:"nf_recebida"})[p.status] || p.status;
              return (
                <tr key={p.id} className="border-b border-border last:border-0" data-testid={`pend-${p.id}`}>
                  <td className="p-3 font-medium">{p.montador_nome}</td>
                  <td className="p-3 font-mono text-xs">{p.periodo}</td>
                  <td className="p-3 font-mono">R$ {p.valor?.toFixed(2)}</td>
                  <td className="p-3 text-xs">{new Date(p.vencimento).toLocaleDateString("pt-BR")}</td>
                  <td className="p-3 font-mono text-xs">{p.nf_numero || "—"}</td>
                  <td className="p-3 text-xs">{p.nf_emitida_em ? new Date(p.nf_emitida_em).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${status.c}`}>{status.l}</span>
                    {canon === "nf_rejeitada" && p.motivo_rejeicao && (
                      <div className="text-[10px] text-red-700 mt-1 max-w-[150px] truncate" title={p.motivo_rejeicao}>
                        Motivo: {p.motivo_rejeicao}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {p.nf_arquivo_path && (
                        <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                          onClick={() => baixar(p, "nf")} data-testid={`pend-download-${p.id}`}>
                          Ver NF
                        </Button>
                      )}
                      {canon === "nf_recebida" && (
                        <>
                          <Button size="sm" className="btn-sharp h-7 text-[10px] bg-[#00A859] text-white hover:bg-[#00874E]"
                            onClick={() => validar(p)} data-testid={`pend-validar-${p.id}`}>
                            Validar
                          </Button>
                          <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px] border-[#E60000] text-[#E60000]"
                            onClick={() => setRejeitar(p)} data-testid={`pend-rejeitar-${p.id}`}>
                            Rejeitar
                          </Button>
                        </>
                      )}
                      {canon === "nf_validada" && (
                        <Button size="sm" className="btn-sharp h-7 text-[10px] brand-bg text-white"
                          onClick={() => { setPagar(p); setFormPagar({ valor_pago: p.valor, data_pagamento: new Date().toISOString().slice(0,10), comprovante: null }); }}
                          data-testid={`pend-pagar-${p.id}`}>
                          Marcar Pago
                        </Button>
                      )}
                      {canon === "nf_paga" && p.comprovante_path && (
                        <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                          onClick={() => baixar(p, "comprovante")} data-testid={`pend-comprovante-${p.id}`}>
                          Comprovante
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog Rejeitar */}
      <Dialog open={!!rejeitar} onOpenChange={(o) => !o && setRejeitar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rejeitar NF</DialogTitle></DialogHeader>
          {rejeitar && (
            <div className="space-y-3">
              <div className="text-sm">
                <strong>{rejeitar.montador_nome}</strong> · Período {rejeitar.periodo} · NF nº {rejeitar.nf_numero}
              </div>
              <div>
                <Label className="label-cap">Motivo da rejeição (será enviado ao Montador)</Label>
                <Textarea value={motivoRejeicao} onChange={(e) => setMotivoRejeicao(e.target.value)}
                  rows={3} placeholder="Ex: CNPJ incorreto, valor divergente, documento ilegível..." data-testid="rej-motivo" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejeitar(null)}>Cancelar</Button>
            <Button onClick={enviarRejeicao} className="bg-[#E60000] text-white hover:bg-red-700"
              data-testid="rej-enviar">Confirmar rejeição</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Pagar */}
      <Dialog open={!!pagar} onOpenChange={(o) => !o && setPagar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar como Pago</DialogTitle></DialogHeader>
          {pagar && (
            <div className="space-y-3">
              <div className="text-sm">
                <strong>{pagar.montador_nome}</strong> · Período {pagar.periodo} · NF nº {pagar.nf_numero}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-cap">Valor pago (R$)</Label>
                  <Input type="number" step="0.01" value={formPagar.valor_pago}
                    onChange={(e) => setFormPagar({ ...formPagar, valor_pago: parseFloat(e.target.value) || 0 })}
                    data-testid="pag-valor" />
                </div>
                <div>
                  <Label className="label-cap">Data do pagamento</Label>
                  <Input type="date" value={formPagar.data_pagamento}
                    onChange={(e) => setFormPagar({ ...formPagar, data_pagamento: e.target.value })}
                    data-testid="pag-data" />
                </div>
              </div>
              <div>
                <Label className="label-cap">Comprovante (PDF/JPG/PNG · 10MB)</Label>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.jfif,.png,.webp,.heic"
                  onChange={(e) => setFormPagar({ ...formPagar, comprovante: e.target.files?.[0] })}
                  data-testid="pag-comprovante" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagar(null)}>Cancelar</Button>
            <Button onClick={enviarPagamento} className="brand-bg text-white" data-testid="pag-enviar">
              Confirmar pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Fechamento manual */}
      <Dialog open={fechamentoOpen} onOpenChange={setFechamentoOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Executar fechamento manual</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Cria pendências NF para todos os montadores com serviços concluídos na janela informada.
              Idempotente (não duplica pendências já geradas). Normalmente o cron diário às 02:30 já cuida disso.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Início</Label>
                <Input type="date" value={janela.inicio}
                  onChange={(e) => setJanela({ ...janela, inicio: e.target.value })}
                  data-testid="fech-inicio" />
              </div>
              <div>
                <Label className="label-cap">Fim</Label>
                <Input type="date" value={janela.fim}
                  onChange={(e) => setJanela({ ...janela, fim: e.target.value })}
                  data-testid="fech-fim" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFechamentoOpen(false)}>Cancelar</Button>
            <Button onClick={executarFechamento} className="brand-bg text-white" data-testid="fech-executar">
              Executar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DashCard({ label, valor, qtd, color }) {
  return (
    <div className="border border-border bg-white p-4">
      <div className="label-cap">{label}</div>
      <div className={`font-heading font-extrabold text-2xl mt-1 ${color}`}>
        R$ {(valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{qtd} pendência(s)</div>
    </div>
  );
}
